import { useState } from "react";
import { useCompanyComments } from "@/hooks/companies/useCompanyComments";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { MessageSquare, Send, Plus, User } from "lucide-react";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";

interface CompanyCommentsProps {
  companyId: string;
}

export const CompanyComments = ({ companyId }: CompanyCommentsProps) => {
  const { comments, loading, addComment } = useCompanyComments(companyId);
  const [newComment, setNewComment] = useState("");
  const [isAddingComment, setIsAddingComment] = useState(false);
  const { t } = useTranslation();

  const handleSubmit = async () => {
    if (!newComment.trim()) return;
    
    await addComment(newComment);
    setNewComment("");
    setIsAddingComment(false);
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <CardContent className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold">{t("companies.detail.tabs.comments")}</h3>
          {!isAddingComment && (
            <Button 
              onClick={() => setIsAddingComment(true)}
              className="bg-digger-blue hover:bg-digger-blue-light"
            >
              <Plus className="h-4 w-4 mr-2" />
              {t("companies.comments.addComment")}
            </Button>
          )}
        </div>
        
        {isAddingComment && (
          <div className="mb-6">
            <Textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder={t("companies.comments.writeComment")}
              className="min-h-[100px] mb-2"
            />
            <div className="flex justify-end gap-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  setIsAddingComment(false);
                  setNewComment("");
                }}
              >
                {t("common.cancel")}
              </Button>
              <Button 
                onClick={handleSubmit}
                disabled={!newComment.trim()}
                className="bg-digger-blue hover:bg-digger-blue-light"
              >
                <Send className="h-4 w-4 mr-2" />
                {t("common.send")}
              </Button>
            </div>
          </div>
        )}
        
        {loading ? (
          <div className="animate-pulse space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-24 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        ) : comments.length > 0 ? (
          <div className="space-y-4">
            {comments.map((comment) => (
              <CommentItem key={comment.id} comment={comment} />
            ))}
          </div>
        ) : (
          <div className="text-center py-8 border rounded-lg">
            <MessageSquare className="h-8 w-8 text-gray-400 mx-auto" />
            <p className="mt-2 text-gray-500">{t("companies.comments.noComments")}</p>
            <Button variant="outline" className="mt-3" onClick={() => setIsAddingComment(true)}>
              <Plus className="h-4 w-4 mr-2" />
              {t("companies.comments.addFirstComment")}
            </Button>
          </div>
        )}
      </CardContent>
    </div>
  );
};

interface CommentItemProps {
  comment: {
    id: string;
    content: string;
    created_at: string;
    created_by: string;
  };
}

const CommentItem = ({ comment }: CommentItemProps) => {
  return (
    <div className="border rounded-lg p-4">
      <div className="flex items-start gap-3">
        <div className="bg-gray-100 rounded-full p-2 mt-1">
          <User className="h-5 w-5 text-gray-500" />
        </div>
        <div className="flex-1">
          <div className="flex justify-between items-center mb-1">
            <h4 className="font-medium">{comment.created_by}</h4>
            <span className="text-sm text-gray-500">
              {format(new Date(comment.created_at), "MMM d, yyyy 'at' h:mm a")}
            </span>
          </div>
          <div className="text-gray-700">{comment.content}</div>
        </div>
      </div>
    </div>
  );
};
